import * as React from "react";
import { useUserNotificationsContext } from "~/config/user/user-notifications/UserNotifications";
import type { UserNotifications } from "~/config/user/user-notifications/utils/types";
import { Box } from "~/design-system/Box";
import { Column, Columns } from "~/design-system/Columns";
import { useBreakpoint } from "~/design-system/hooks";
import { Stack } from "~/design-system/Stack";
import { LoadButton } from "~/screens/App/components/LoadButton/LoadButton";
import { ResetFilterButton } from "~/screens/App/screens/Project/Tasks/screens/ProjectTasksListScreen/components/ProjectTasksList/components/ResetFilterButton";
import { createComponentHook } from "~/types";
import type { Filters } from "../../../UserNotificationsScreen";
import { getDisplayNotificationCount } from "./components/helpers";
import { NotificationsFilter } from "./components/NotificationsFilter/NotificationsFilter";
import { NotificationsListDesktop } from "./components/NotificationsList/NotificationsListDesktop";
import { NotificationsListMobile } from "./components/NotificationsList/NotificationsListMobile";

type NotificationsListProps = {
  notifications?: UserNotifications;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  filters: Filters;
  defaultFilters?: Filters;
  fetchMore?: () => void;
};

const useNotificationsList = createComponentHook(
  (props: NotificationsListProps) => {
    const [buttonToShow, setButtonToShow] = React.useState<
      "cleanFilter" | "loadMore" | "noButton"
    >("loadMore");

    function areFiltersEqual(filterA: Filters, filterB: Filters): boolean {
      const categoriesEqual =
        JSON.stringify(Object.entries(filterA.categories).sort()) ===
        JSON.stringify(Object.entries(filterB.categories).sort());

      const projectIdsEqual =
        JSON.stringify(filterA.projectIds.slice().sort()) ===
        JSON.stringify(filterB.projectIds.slice().sort());

      return categoriesEqual && projectIdsEqual;
    }

    const all_notifications = useUserNotificationsContext(
      (state) => state.notifications
    );

    const { strongNotificationsCount, weakNotificationsCount } =
      getDisplayNotificationCount(all_notifications);

    React.useEffect(() => {
      const allDataFetched =
        (props.filters.types.strong &&
          !props.filters.types.weak &&
          props?.notifications?.length === strongNotificationsCount) ||
        (props.filters.types.weak &&
          !props.filters.types.strong &&
          props?.notifications?.length === weakNotificationsCount) ||
        (props.filters.types.strong &&
          props.filters.types.weak &&
          props?.notifications?.length ===
            strongNotificationsCount + weakNotificationsCount);

      if (props.notifications?.length === 0 || allDataFetched) {
        setButtonToShow("noButton");
      } else if (areFiltersEqual(props.filters, props.defaultFilters!)) {
        setButtonToShow("loadMore");
      } else {
        setButtonToShow("cleanFilter");
      }
    }, [props.filters]);

    return {
      actions: {
        setButtonToShow,
      },
      state: {
        buttonToShow,
      },
    };
  }
);

const NotificationsList = (props: NotificationsListProps) => {
  const { breakpoint } = useBreakpoint();

  const { state } = useNotificationsList(props);

  if (breakpoint === "mobile") {
    return (
      <>
        <NotificationsListMobile notifications={props.notifications} />
        <Box padding="gutter">
          <Stack space="small" align="center">
            {state.buttonToShow === "loadMore" && (
              <LoadButton
                loading={false}
                onClick={() => {
                  props.fetchMore?.();
                }}
              />
            )}
            {state.buttonToShow === "cleanFilter" && (
              <ResetFilterButton
                onClick={() => {
                  props.setFilters(props.defaultFilters!);
                }}
              />
            )}
          </Stack>
        </Box>
      </>
    );
  } else {
    return (
      <Columns
        space="gutter"
        collapseBelow="desktop"
        reverse={breakpoint === "tablet"}
      >
        <Column width="2-3">
          <NotificationsListDesktop notifications={props.notifications} />
          <Box padding="gutter">
            <Stack space="small" align="center">
              {state.buttonToShow === "loadMore" && (
                <LoadButton
                  loading={false}
                  onClick={() => {
                    props.fetchMore?.();
                  }}
                />
              )}
              {state.buttonToShow === "cleanFilter" && (
                <ResetFilterButton
                  onClick={() => {
                    props.setFilters(props.defaultFilters!);
                  }}
                />
              )}
            </Stack>
          </Box>
        </Column>
        <Column>
          <NotificationsFilter
            setFilters={props.setFilters}
            filters={props.filters}
          />
        </Column>
      </Columns>
    );
  }
};

export { NotificationsList };
